import { CartItem, CartModifierGroup } from './cart';
import { ParsedMenuItem } from '../types/menu';
import { UnavailableItemsByName } from '../redux/features/menuUnification/menuUnification.props';
import { isBMModifierAnUnavailableConstraint } from './menuUnification.utils';
import { getParentModifier } from './menu';
import { getOptionModifierGroups } from './lazyOptionBuild';

/**
 * Finds a menu item in a modifier group using multiple lookup strategies.
 * Tries direct key access first, then searches by name, itemId, or id.
 *
 * @param modifierGroup - The modifier group to search in
 * @param searchKeys - Array of keys to try for direct lookup (e.g., itemId, name)
 * @param searchValue - The value to match against item name, itemId, or id
 * @returns The found menu item, or undefined if not found
 */
const findMenuItemInModifierGroup = (
  modifierGroup: CartItem['modifierGroups'][string],
  searchKeys: (string | undefined)[],
  searchValue: string
): ParsedMenuItem | undefined => {
  // Try direct key access first
  for (const key of searchKeys) {
    if (key && modifierGroup.menuItems?.[key]) {
      return modifierGroup.menuItems[key];
    }
  }

  // Fallback to searching by multiple fields
  const foundMenuItem = Object.values(modifierGroup.menuItems || {}).find(
    (item) =>
      item.name === searchValue ||
      item.itemId === searchValue ||
      item.id === searchValue
  );

  return foundMenuItem;
};

const buildCartItemWithNewSelection = (
  cartItem: CartItem,
  menuItem: ParsedMenuItem,
  contextModifierGroup: CartItem['modifierGroups'][string],
  contextModifierGroupId: string,
  existingContextGroup: CartModifierGroup | undefined,
  optionName: string
): CartItem => {
  // Create a proper CartItem for the new selection
  const newSelectedItem: CartItem = {
    ...menuItem,
    // HITL-1748: as at a real selection, a lazy option gets its nested groups.
    ...(menuItem.lazyGroupsRef
      ? {
          modifierGroups: getOptionModifierGroups(menuItem),
          // The copy now carries its own groups; nothing should rebuild them.
          lazyGroupsRef: undefined,
        }
      : null),
    cartItemId: -1, // Temporary ID
    modality: cartItem.modality,
    childModifierGroups: {},
  };

  // Determine if this is a single-selection modifier group (max === 1)
  // If single-selection, replace existing selections; otherwise merge
  const isSingleSelection = contextModifierGroup.maximumSelections === 1;

  // Build the selectedItems based on single vs multiple selection
  // Use the item name as the key (not itemId) to match constraint checking logic
  const selectedItemKey = newSelectedItem.name || optionName;
  const selectedItems = isSingleSelection
    ? {
        // Single selection: replace all existing selections
        [selectedItemKey]: newSelectedItem,
      }
    : {
        // Multiple selection: merge with existing selections
        ...existingContextGroup?.selectedItems,
        [selectedItemKey]: newSelectedItem,
      };

  // Merge with existing group, preserving all properties
  return {
    ...cartItem,
    childModifierGroups: {
      ...cartItem.childModifierGroups,
      [contextModifierGroupId]: existingContextGroup
        ? {
            ...existingContextGroup,
            selectedItems,
          }
        : ({
            ...contextModifierGroup,
            cartModifierGroupId: contextModifierGroupId,
            menuModifierGroupId: contextModifierGroupId,
            selectedItems,
          } as CartModifierGroup),
    },
  };
};

/**
 * Checks if a target modifier group has invalid selections.
 * A selection is invalid if:
 * 1. There's no existing selection, OR
 * 2. Any existing selection is unavailable (either by menu item availability or by constraint)
 *
 * When checking constraints, includes the newly selected item in the cart item context
 * to see if the combination makes the existing selection invalid.
 *
 * @param state - The target state containing modifier group and selected items
 * @param cartItem - The cart item to check
 * @param target - The target modifier group name
 * @param unavailableItemsByName - Mapping of unavailable items
 * @param selectedContext - Optional context of the newly selected item
 * @returns true if the selection is invalid, false otherwise
 */
export const hasInvalidSelectionForTarget = (
  state: {
    modifierGroup: CartItem['modifierGroups'][string];
    selectedItems: CartItem[];
    hasExistingSelection: boolean;
  },
  cartItem: CartItem,
  target: string,
  unavailableItemsByName: UnavailableItemsByName,
  selectedContext?: {
    modifierGroupName: string;
    optionName: string;
  }
): boolean => {
  // Check if there's no selection OR if existing selections are unavailable
  // Also check if existing selections become invalid when combined with the newly selected item

  // Build cartItemForValidation once before the loop since it only depends on
  // cartItem and selectedContext (both constant across iterations)
  // This avoids reconstructing the same cart item multiple times
  let cartItemForValidation = cartItem;
  if (selectedContext) {
    // Find the modifier group ID for the selected context
    const contextModifierGroup = Object.values(
      cartItem.modifierGroups || {}
    ).find((group) => group.name === selectedContext.modifierGroupName);
    const contextModifierGroupId = contextModifierGroup?.id;

    if (contextModifierGroupId) {
      // Create a cart item that includes the new selection
      const existingContextGroup =
        cartItem.childModifierGroups?.[contextModifierGroupId];

      // Find the menu item to get its full properties
      // Try multiple lookup strategies: by optionName as key, by name, or by itemId
      const menuItem = contextModifierGroup
        ? findMenuItemInModifierGroup(
            contextModifierGroup,
            [selectedContext.optionName],
            selectedContext.optionName
          )
        : undefined;

      if (menuItem) {
        cartItemForValidation = buildCartItemWithNewSelection(
          cartItem,
          menuItem,
          contextModifierGroup,
          contextModifierGroupId,
          existingContextGroup,
          selectedContext.optionName
        );
      }
    }
  }

  return (
    !state.hasExistingSelection ||
    state.selectedItems.some((selectedItem) => {
      // Check if the selected item is unavailable by itself
      // Look up the menu item to check its availability
      const menuItem = findMenuItemInModifierGroup(
        state.modifierGroup,
        [selectedItem.itemId, selectedItem.name],
        selectedItem.itemId || selectedItem.name || ''
      );
      const isUnavailableByMenuItem =
        selectedItem.available === false ||
        (menuItem !== undefined && menuItem.available === false);
      if (isUnavailableByMenuItem) {
        return true;
      }

      // Check if the selected item is unavailable by constraint
      // Use the pre-built cartItemForValidation to avoid reconstruction
      // For brand menu, use the item name (not itemId) for constraint checks
      const itemNameForCheck = selectedItem.name || selectedItem.itemId;

      const isUnavailableByConstraint = isBMModifierAnUnavailableConstraint(
        cartItemForValidation,
        unavailableItemsByName,
        getParentModifier(selectedItem.pathToItem || ''),
        selectedItem.pathToItem || '',
        state.modifierGroup.name || target,
        itemNameForCheck
      );
      return isUnavailableByConstraint;
    })
  );
};
